import { Box, Button, Heading, NumberField, Select, Stack, TextField, Tooltip } from '@eleveo/components-react';
import { MinusIcon, PlusIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React, { useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';

import { ADD_CUSTOM_QUEUE_TAG_BUTTON_TAG, getFieldsInputTag } from './constants';
import { formatQueueTag, normalizeAcdQueues } from './queuesUtils';
import { QueueChannelType, Skill, Timezone } from '../../graphql/components';
import { AcdQueuesResult, QueuesTagsResult, SkillsAllResult, TimezonesAllResult } from '../../graphql/types';
import { SortOrder } from '../../types';
import { shouldShowSearchField } from '../../utils/helpers';
import { customSort } from '../../utils/sort';
import { formatTimezoneIdToName } from '../../utils/timezoneFormatter';
import { max255chars } from '../../utils/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import {
  intervalValidation,
  maxChars,
  percentageValidation,
  required,
  tagAllowedCharactersValidation,
} from '../../utils/validations/queuesValidators';

export const MAX_QUEUE_TAG_LENGTH = 30;

const timezonesToOptions = (timezonesResult: TimezonesAllResult) =>
  (timezonesResult.data?.timezones ?? []).map(({ zoneId }: Timezone) => ({
    id: zoneId,
    name: formatTimezoneIdToName(zoneId),
  }));

const skillsToOptions = (skills: SkillsAllResult) =>
  (skills.data?.skills ?? []).map((skill: Skill) => ({
    id: skill.id,
    name: skill.name,
  }));

const channelTypeOptions = (t: TFunction) =>
  Object.values(QueueChannelType).map((type) => ({
    id: type,
    name: t(`queue.channelType.${type}`),
  }));

interface QueueFormProps {
  acdQueueIdsEditable: boolean;
  acdQueuesResult: AcdQueuesResult;
  channelTypeEditable: boolean;
  nameEditable: boolean;
  serviceLevelEditable: boolean;
  skillIdEditable: boolean;
  skillsResult: SkillsAllResult;
  tagEditable: boolean;
  tagsResult: QueuesTagsResult;
  timezoneEditable: boolean;
  timezonesResult: TimezonesAllResult;
  t: TFunction;
}

export const QueueForm: React.FC<QueueFormProps> = ({
  acdQueueIdsEditable,
  acdQueuesResult,
  channelTypeEditable,
  nameEditable,
  serviceLevelEditable,
  skillIdEditable,
  skillsResult,
  t,
  tagsResult,
  tagEditable,
  timezoneEditable,
  timezonesResult,
}) => {
  const [isCustomTagValueFieldVisible, setCustomTagValueFieldVisible] = useState(false);

  const form = useForm();

  const nameField = useField('name', {
    validate: composeValidators(required, max255chars),
  });

  const channelTypeField = useField('channelType', {
    validate: required,
  });

  const tagField = useField('tag', {
    validate: composeValidators(required, maxChars(MAX_QUEUE_TAG_LENGTH), tagAllowedCharactersValidation),
  });

  const acdQueueIdsField = useField('acdQueueIds');

  const acdQueueOptions = customSort(
    normalizeAcdQueues(acdQueuesResult.data?.acdQueues ?? []),
    ['name'],
    SortOrder.ASCENDING
  );

  const timezoneField = useField('timezone', {
    validate: required,
  });

  const timezoneOptions = customSort(timezonesToOptions(timezonesResult), ['name'], SortOrder.ASCENDING);

  const skillField = useField('skillId');

  const skillsOptions = skillsToOptions(skillsResult);

  const percentageField = useField<number>('percentage', {
    // Needed when comparing "NaN"s
    isEqual: (a, b) => Object.is(a, b),
    validate: percentageValidation,
  });

  const intervalField = useField<number>('interval', {
    // Needed when comparing "NaN"s
    isEqual: (a, b) => Object.is(a, b),
    validate: intervalValidation,
  });

  /**
   * Workaround to avoid removing not-rendered input components from react-final-form state.
   * Original checkboxes for assigning agents are located in ./QueuesBulkAssignForm.tsx.
   *
   * @see https://github.com/final-form/react-final-form/issues/621
   * @see https://github.com/final-form/react-final-form/issues/626
   */
  useField('assignedIds', {
    type: 'checkbox',
  });

  const tagsItems = useMemo(
    () => (tagsResult?.data?.queuesTags ?? []).map(({ value }) => ({ id: value, name: value })),
    [tagsResult?.data?.queuesTags]
  );

  const handleToggleCustomTagValueFieldVisible = () => {
    if (isCustomTagValueFieldVisible) {
      setCustomTagValueFieldVisible(false);
    } else {
      tagField.input.onChange('');
      form.resetFieldState('tag'); // resets invalid state so that field is not marked as invalid when appears after toggle
      setCustomTagValueFieldVisible(true);
    }
  };

  const handleSelectExistingTagFromList = (tag?: string) => {
    if (isCustomTagValueFieldVisible) {
      setCustomTagValueFieldVisible(false);
    }

    tagField.input.onChange(tag);
  };

  return (
    <Box padding={20}>
      <Stack direction="vertical" className="w-full" spacing={25}>
        <TextField
          {...nameField.input}
          className="w-full"
          isDisabled={!nameEditable}
          label={t('form.name')}
          testId={getFieldsInputTag('queue-name')}
          validationState={nameField.meta.touched && nameField.meta.invalid ? 'invalid' : undefined}
        />

        <Select
          className="w-full"
          isDisabled={!channelTypeEditable}
          label={t('queue.channelType.channelType')}
          onBlur={channelTypeField.input.onBlur}
          onFocus={channelTypeField.input.onFocus}
          onSelectionChange={(values) => channelTypeField.input.onChange(values.values().next().value)}
          options={channelTypeOptions(t)}
          selectedKeys={channelTypeField.input.value ? [channelTypeField.input.value] : []}
          selectionMode="single"
          testId={getFieldsInputTag('channel-type-dropdown')}
          validationState={channelTypeField.meta.touched && channelTypeField.meta.invalid ? 'invalid' : undefined}
        />

        <Stack direction="vertical" spacing={15}>
          <Stack spacing={15}>
            <Select
              className="w-full"
              errorMessage={isCustomTagValueFieldVisible ? undefined : t(tagField.meta.error)}
              isDisabled={!tagEditable}
              isSearchable={shouldShowSearchField(tagsItems.length)}
              label={t('queue.selectExistingQueueTag')}
              onBlur={tagField.input.onBlur}
              onFocus={tagField.input.onFocus}
              onSelectionChange={(values) => handleSelectExistingTagFromList(values.values().next().value as string)}
              options={tagsItems}
              selectedKeys={
                !isCustomTagValueFieldVisible && !tagsResult.loading && tagField.input.value
                  ? [tagField.input.value]
                  : []
              }
              selectionMode="single"
              testId={getFieldsInputTag('queue-tag:select-from-existing-tags')}
              validationState={
                !isCustomTagValueFieldVisible && tagField.meta.touched && tagField.meta.invalid ? 'invalid' : undefined
              }
            />

            <Tooltip content={t('form.addNew')}>
              <Button
                aria-label={t('form.addNew')}
                color={isCustomTagValueFieldVisible ? 'warning' : undefined}
                isDisabled={!tagEditable}
                onClick={handleToggleCustomTagValueFieldVisible}
                testId={ADD_CUSTOM_QUEUE_TAG_BUTTON_TAG}
                variant="outline"
              >
                {isCustomTagValueFieldVisible ? <MinusIcon /> : <PlusIcon />}
              </Button>
            </Tooltip>
          </Stack>

          {isCustomTagValueFieldVisible && (
            <TextField
              {...tagField.input}
              autoFocus
              className="w-full"
              isDisabled={!tagEditable}
              label={t('queue.createNewQueueTag')}
              onChange={(value: string) => tagField.input.onChange(formatQueueTag(value))}
              testId={getFieldsInputTag('queue-tag:add-new')}
              validationState={tagField.meta.touched && tagField.meta.invalid ? 'invalid' : undefined}
              errorMessage={t(tagField.meta.error, {
                characters: MAX_QUEUE_TAG_LENGTH, // used for "validation.maxLength" error message
              })}
            />
          )}
        </Stack>

        <Select
          className="w-full"
          isClearable
          isDisabled={!acdQueueIdsEditable}
          isSearchable={shouldShowSearchField(acdQueueOptions.length)}
          label={t('queue.acdQueue')}
          onSelectionChange={(values) => acdQueueIdsField.input.onChange(Array.from(values))}
          options={acdQueueOptions}
          selectedKeys={acdQueueIdsField.input.value}
          selectionMode="multiple"
          testId={getFieldsInputTag('acd-queue-dropdown')}
        />

        <Select
          className="w-full"
          isDisabled={!timezoneEditable}
          isSearchable={shouldShowSearchField(timezoneOptions.length)}
          label={t('timezone.timezone')}
          onBlur={timezoneField.input.onBlur}
          onFocus={timezoneField.input.onFocus}
          onSelectionChange={(values) => timezoneField.input.onChange(values.values().next().value)}
          options={timezoneOptions}
          selectedKeys={timezoneField.input.value ? [timezoneField.input.value] : []}
          selectionMode="single"
          testId={getFieldsInputTag('timezone-dropdown')}
          validationState={timezoneField.meta.touched && timezoneField.meta.invalid ? 'invalid' : undefined}
        />

        <Select
          className="w-full"
          isClearable
          isDisabled={!skillIdEditable}
          isSearchable={shouldShowSearchField(skillsOptions.length)}
          label={t('skill.skill')}
          onSelectionChange={(values) => skillField.input.onChange(values.values().next().value)}
          options={skillsOptions}
          selectedKeys={skillField.input.value ? [skillField.input.value] : []}
          selectionMode="single"
          testId={getFieldsInputTag('skill-dropdown')}
        />

        <Heading as="h3" className="text-blue-700">
          {t('queue.serviceLevel')}
        </Heading>

        <Stack className="w-full" spacing={15}>
          <NumberField
            {...percentageField.input}
            // TODO: Decide what to do with format options and description duplication
            description={t('queue.percentageDescription')}
            formatOptions={{
              style: 'percent',
            }}
            isDisabled={!serviceLevelEditable}
            label={t('queue.percentage')}
            maxValue={1}
            minValue={0}
            testId={getFieldsInputTag('service-level-percentage')}
            validationState={percentageField.meta.touched && percentageField.meta.invalid ? 'invalid' : undefined}
          />

          <NumberField
            {...intervalField.input}
            // TODO: Decide what to do with format options and description duplication
            description={t('time.secondsDescription')}
            formatOptions={{
              style: 'unit',
              unit: 'second',
            }}
            isDisabled={!serviceLevelEditable}
            label={t('time.interval')}
            maxValue={28800}
            minValue={1}
            testId={getFieldsInputTag('service-level-interval')}
            validationState={intervalField.meta.touched && intervalField.meta.invalid ? 'invalid' : undefined}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
